import { Drawer, Hide, useMobileMediaQuery } from '@iheartjane/reefer';
import { useEffect, useRef, useState } from 'react';

import { CartAlerts } from '@jane/monolith/components/cartAlerts';
import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import { closeCart, selectCart } from '@jane/monolith/customer/redux/cart';
import { openModal } from '@jane/monolith/customer/redux/modal';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { useBrandSpecialsContext } from '@jane/shared-ecomm/providers';
import { useEcommApp } from '@jane/shared/providers';

import { CartDrawerContainer } from './cartDrawer/cartDrawerContainer';

export const MarketplaceCartDrawer = ({
  container = 'body',
}: {
  container?: string | HTMLElement;
}) => {
  const [showCartAlerts, setShowCartAlerts] = useState<boolean>(false);
  const isMobile = useMobileMediaQuery({});
  const dispatch = useCustomerDispatch();
  const { isEmbed } = useEcommApp();
  const {
    cart,
    isRemovingSpecialHasChangedFlag,
    isDeletingUnavailableProducts,
  } = useCustomerSelector(selectCart);
  const { showBrandSpecialAlert } = useBrandSpecialsContext();

  const [cartMessage, setCartMessage] = useState<string>();

  const {
    is_open,
    special_has_changed,
    unavailable_products,
    removed_crm_redemptions,
    products,
  } = cart;
  const isCartOpen = !!is_open;
  const prevProducts = useRef(products || []);

  const cartHasChanged =
    showBrandSpecialAlert ||
    (special_has_changed && !isRemovingSpecialHasChangedFlag) ||
    (unavailable_products &&
      unavailable_products.length &&
      !isDeletingUnavailableProducts) ||
    (removed_crm_redemptions && removed_crm_redemptions.length > 0);

  useEffect(() => {
    /**
     * We don't want to trigger openModal on subdomains so we need
     * to also check if we are on embeddedMode
     */
    if (isCartOpen && cartHasChanged && !isEmbed) {
      dispatch(
        openModal({
          name: 'cartAlerts',
        })
      );
    }
  }, [isCartOpen, cartHasChanged]);

  useEffect(() => {
    const currentProductLength = products?.length || 0;
    const productAdded = currentProductLength > prevProducts.current.length;
    const productRemoved = prevProducts.current.length > currentProductLength;

    if (productAdded && isCartOpen) {
      setCartMessage(
        `${products[products.length - 1].name} added to your bag.`
      );
    } else if (productRemoved && isCartOpen) {
      const removedProductName = prevProducts.current.find(
        (prevProduct) =>
          !products?.some((product) => product.id === prevProduct.id)
      )?.name;
      setCartMessage(
        `${removedProductName ?? 'product'} removed from your bag.`
      );
    } else if (!isCartOpen) {
      setCartMessage('');
    }
    prevProducts.current = products || [];
  }, [products, isCartOpen]);

  return (
    <>
      <Drawer
        data-testid="cart-drawer"
        container={container}
        width={isMobile ? '95vw' : '540px'}
        open={isCartOpen}
        onRequestClose={() => dispatch(closeCart())}
        background="grays-ultralight"
      >
        <Hide isHidden aria-live="polite" aria-atomic="true">
          {cartMessage}
        </Hide>
        <CartDrawerContainer setShowCartAlerts={setShowCartAlerts} />
        {isEmbed && showCartAlerts && (
          <CartAlerts
            onClose={() => setShowCartAlerts(false)}
            isCheckoutAlert
          />
        )}
      </Drawer>
    </>
  );
};
